/**
 * Hole-by-hole helpers shared by the By-Hole view and the scorecard review:
 * nine / round sums, score vs par, and the golf name for a score (used for the
 * birdie-circle / bogey-square marks).
 */

export type Cell = number | null | undefined;
export type Nine = 'front' | 'back';

export const FRONT = [1, 2, 3, 4, 5, 6, 7, 8, 9];
export const BACK = [10, 11, 12, 13, 14, 15, 16, 17, 18];
export const ALL_HOLES = [...FRONT, ...BACK];
export const NINE_HOLES: Record<Nine, number[]> = { front: FRONT, back: BACK };

const isNum = (v: Cell): v is number => typeof v === 'number' && Number.isFinite(v);

/** Sum of `values` over `holes` (1-based); null unless every hole has a value. */
export function sumHoles(values: Cell[] | undefined, holes: number[]): number | null {
  if (!values || !holes.length) return null;
  let sum = 0;
  for (const h of holes) {
    const v = values[h - 1];
    if (!isNum(v)) return null;
    sum += v;
  }
  return sum;
}

/** Sum of whatever is filled in, and how many holes that is. */
export function sumFilled(values: Cell[] | undefined, holes: number[]): { sum: number; count: number } {
  let sum = 0;
  let count = 0;
  for (const h of holes) {
    const v = values?.[h - 1];
    if (isNum(v)) { sum += v; count += 1; }
  }
  return { sum, count };
}

/** Holes that belong to a nine AND were played. */
export const nineHoles = (nine: Nine, played: number[] = ALL_HOLES) => NINE_HOLES[nine].filter((h) => played.includes(h));

/** The nines that were played (a 9-hole round has one). */
export function playedNines(played: number[] = ALL_HOLES): Nine[] {
  return (['front', 'back'] as Nine[]).filter((n) => nineHoles(n, played).length > 0);
}

export interface RowTotals { out: number | null; in: number | null; total: number | null; complete: boolean }

export function rowTotals(strokes: Cell[] | undefined, played: number[] = ALL_HOLES): RowTotals {
  const front = nineHoles('front', played);
  const back = nineHoles('back', played);
  const total = sumHoles(strokes, played);
  return {
    out: front.length ? sumHoles(strokes, front) : null,
    in: back.length ? sumHoles(strokes, back) : null,
    total,
    complete: total != null,
  };
}

export function relToPar(strokes: Cell, par: Cell): number | null {
  return isNum(strokes) && isNum(par) ? strokes - par : null;
}

/** "E", "+2", "-1" */
export function formatRel(rel: number | null | undefined): string {
  if (rel == null) return '';
  if (rel === 0) return 'E';
  return rel > 0 ? `+${rel}` : `${rel}`;
}

export type ScoreKind = 'albatross' | 'eagle' | 'birdie' | 'par' | 'bogey' | 'double' | 'worse';

export function scoreKind(strokes: Cell, par: Cell): ScoreKind | null {
  const rel = relToPar(strokes, par);
  if (rel == null) return null;
  if (strokes === 1) return 'eagle'; // hole in one: celebrate it like an eagle at least
  if (rel <= -3) return 'albatross';
  if (rel === -2) return 'eagle';
  if (rel === -1) return 'birdie';
  if (rel === 0) return 'par';
  if (rel === 1) return 'bogey';
  if (rel === 2) return 'double';
  return 'worse';
}

const KIND_LABEL: Record<ScoreKind, string> = {
  albatross: 'Albatross', eagle: 'Eagle', birdie: 'Birdie', par: 'Par', bogey: 'Bogey', double: 'Double', worse: '',
};

/** Label for a quick-pick relative score: "Birdie", "Par", "+3". */
export function relLabel(rel: number): string {
  if (rel <= -3) return KIND_LABEL.albatross;
  if (rel === -2) return KIND_LABEL.eagle;
  if (rel === -1) return KIND_LABEL.birdie;
  if (rel === 0) return KIND_LABEL.par;
  if (rel === 1) return KIND_LABEL.bogey;
  if (rel === 2) return KIND_LABEL.double;
  return `+${rel}`;
}

/** Holes whose strokes are filled in (or null when none are). */
export function filledHoles(strokes: Cell[] | undefined): number[] {
  return ALL_HOLES.filter((h) => isNum(strokes?.[h - 1]));
}

/** Played holes for a round: all 18, or the nine the scores are on. */
export function inferPlayedHoles(numberOfHoles: number | null | undefined, rows: Cell[][]): number[] {
  if (Number(numberOfHoles) !== 9) return ALL_HOLES;
  let front = 0;
  let back = 0;
  for (const r of rows) for (const h of filledHoles(r)) { if (h <= 9) front += 1; else back += 1; }
  return back > front ? BACK : FRONT;
}
